import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCardsStore, type NavigationContext } from '@/stores/cardsStore'

export type BoundaryDirection = 'up' | 'down' | 'left' | 'right' | null

interface UseKeyboardNavigationResult {
  navContext: NavigationContext | null
  boundaryHit: BoundaryDirection
  navigateTo: (lid: string) => void
}

export function useKeyboardNavigation(currentLid: string): UseKeyboardNavigationResult {
  const navigate = useNavigate()
  const { getNavigationContext, cards } = useCardsStore()
  const [boundaryHit, setBoundaryHit] = useState<BoundaryDirection>(null)

  const navContext = useMemo(() => {
    return getNavigationContext(currentLid)
  }, [currentLid, cards, getNavigationContext])

  const navigateTo = useCallback(
    (lid: string) => {
      navigate(`/card/${encodeURIComponent(lid)}`)
    },
    [navigate]
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if in input/textarea/contenteditable
      const target = e.target as HTMLElement
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return
      }

      if (!navContext) return

      let targetLid: string | null = null
      let direction: BoundaryDirection = null

      switch (e.key) {
        case 'ArrowUp':
        case 'k':
        case 'PageUp':
          direction = 'up'
          targetLid = navContext.prevSibling?.lid || null
          break
        case 'ArrowDown':
        case 'j':
        case 'PageDown':
          direction = 'down'
          targetLid = navContext.nextSibling?.lid || null
          break
        case 'ArrowLeft':
        case 'h':
          direction = 'left'
          targetLid = navContext.parent?.lid || null
          break
        case 'ArrowRight':
        case 'l':
          direction = 'right'
          targetLid = navContext.firstChild?.lid || null
          break
        default:
          return
      }

      e.preventDefault()

      if (targetLid) {
        navigateTo(targetLid)
      } else {
        // Trigger boundary feedback
        setBoundaryHit(direction)
        setTimeout(() => setBoundaryHit(null), 200)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navContext, navigateTo])

  return { navContext, boundaryHit, navigateTo }
}
